import { Checkbox, LinkButton, Modal } from '@pmi/dsm-react';
import { ChevronLeftSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import { useState } from 'react';
import { toastWithAnalytics } from '../../../shared/analytics';
import CategoryLabel from '../../../shared/components/CategoryLabel';
import agreementCardStyles from '../../../shared/components/InviteWizard/agreementCard.module.scss';
import AgreementSelectedProducts from '../../../shared/components/InviteWizard/AgreementSelectedProducts';
import ProductQuantityInfo, {
  getLowQuantityThreshold,
} from '../../../shared/components/InviteWizard/ProductQuantityInfo';
import selectContainerStyles from '../../../shared/components/InviteWizard/selectAgreement.module.scss';
import wizardStyles from '../../../shared/components/InviteWizard/wizard.module.scss';
import RenderHtml from '../../../shared/components/RenderHtml';
import { AccessRuleItems } from '../../../shared/models/AccessRule';
import { AgreementApiModel } from '../../../shared/models/DealModel';
import { ProductApiModel } from '../../../shared/models/ProductModel';
import getNetQuantity from '../../../shared/utils/getNetQuantity';
import ProductDetailModal from '../../Dashboard/DealTabs/DealProductsTable/ProductDetailModal';
import { useAddProductsToDomainMutation } from './apiSlice';

interface AddProductsToDomainModalProps {
  deal: AgreementApiModel;
  rule: AccessRuleItems;
  onClose: () => void;
  onSuccess?: () => void;
  disabledProductIds?: number[];
}

export default function AddProductsToDomainModal({
  deal,
  rule,
  onClose,
  onSuccess = undefined,
  disabledProductIds = [],
}: AddProductsToDomainModalProps) {
  const [selectedProducts, setSelectedProducts] = useState<ProductApiModel[]>(
    [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addProductsToDomain] = useAddProductsToDomainMutation();

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) return;

    setIsSubmitting(true);
    try {
      const { accountNumber, providerNumber, id, priority, createdByPersonId } =
        rule;
      await addProductsToDomain({
        accountNumber,
        providerNumber,
        accessRuleId: id,
        priority,
        accountProductIds: selectedProducts.map((p) => p.accountProductId),
        createdByPersonId,
      }).unwrap();

      toastWithAnalytics(
        `${selectedProducts.length} product(s) added to domain successfully`,
        'success',
      );
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error: any) {
      let errorMessage = 'Failed to add products to domain. Please try again.';
      try {
        if (error?.data?.Value) {
          const parsedError = JSON.parse(error.data.Value);
          errorMessage = parsedError.error || errorMessage;
        }
      } catch {
        // Use default error message
      }
      toastWithAnalytics(errorMessage, 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<
    ProductApiModel | undefined
  >(undefined);

  const products = deal.products.filter((x) => !x.retired) ?? [];
  const displayedProducts = products.filter(
    (p) => !disabledProductIds!.includes(p.accountProductId),
  );

  const isSelected = (product: ProductApiModel) =>
    selectedProducts.find((p) => p.sku === product.sku);

  const onToggle = (remove: boolean, product: ProductApiModel) => {
    if (!remove) {
      setSelectedProducts([...selectedProducts, product]);
    } else {
      setSelectedProducts(
        selectedProducts.filter((p) => p.sku !== product.sku),
      );
    }
  };

  const onMoreDetailsClick = (product: ProductApiModel) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  };

  return (
    <Modal
      show
      customClassName={`m-0 ${wizardStyles.wizardModal}`}
      variant="input"
      onClose={onClose}
    >
      <div className="wizard-container">
        {/** add a placeholder for stepper here so it's align to other invite wizard screens */}
        <div className="stepper-container my-3" />
        <div className="wizard-container-inner">
          <div className={selectContainerStyles.selectContainer}>
            <LinkButton
              size="sm"
              className="wizard-back-btn mb-4"
              icon={ChevronLeftSolidIcon}
              iconAlign="left"
              titleText="Back to Settings"
              onClick={() => onClose()}
            />
          </div>
          <div className={agreementCardStyles.agreementProductSelector}>
            <h3>Select one or more products</h3>
            <hr className="mt-3 mb-4" />
            <div className="d-flex gap-5">
              <div className="d-block flex-grow-1">
                <div className="d-flex gap-4 mb-2">
                  <LinkButton
                    titleText="Select All Products"
                    size="sm"
                    variant="light"
                    className={`${agreementCardStyles.btnSelectAllProducts} p-0`}
                    onClick={() => {
                      const available = displayedProducts.filter(
                        (p) => deal.isPayByUser || p.quantityRemaining > 0,
                      );
                      setSelectedProducts(available);
                    }}
                  />
                  {selectedProducts.length >= 2 && (
                    <LinkButton
                      titleText="Clear Selected"
                      size="sm"
                      variant="light"
                      className={`${agreementCardStyles.btnSelectAllProducts} p-0`}
                      onClick={() => setSelectedProducts([])}
                    />
                  )}
                </div>
                <ul className={agreementCardStyles.productCardUl}>
                  {displayedProducts.map((p) => {
                    const threshold = getLowQuantityThreshold(
                      getNetQuantity(p),
                    );
                    const isLowQuantity =
                      p.quantityRemaining / getNetQuantity(p) <= threshold;
                    return (
                      <li
                        className={`${agreementCardStyles.productBlockCard} ${
                          isSelected(p)
                            ? agreementCardStyles.productBlockCardActive
                            : ''
                        } mb-3 rounded-1`}
                        key={`domainProdSel${p.sku}`}
                      >
                        {!deal.isPayByUser && (
                          <ProductQuantityInfo
                            total={getNetQuantity(p)}
                            available={p.quantityRemaining}
                          />
                        )}
                        <div
                          className={`d-flex ${agreementCardStyles.content}`}
                        >
                          <div className="d-block border-end align-items-center align-content-center pe-2">
                            <Checkbox
                              disabled={
                                !deal.isPayByUser && p.quantityRemaining <= 0
                              }
                              onChange={(_event, checked) =>
                                onToggle(!checked, p)
                              }
                              checked={!!isSelected(p)}
                              value={p.sku}
                            />
                          </div>
                          <div className="d-flex flex-column px-3 flex-grow-1">
                            <div className="d-block">
                              <CategoryLabel type={p.type} className="mb-2" />
                            </div>
                            <div className="d-block">
                              <RenderHtml
                                html={
                                  p?.name && p.name.length > 0 ? p.name : p.sku
                                }
                                tag="b"
                              />
                            </div>
                            <div
                              className={`d-block ${agreementCardStyles.productDescriptionEllipsis}`}
                            >
                              <RenderHtml
                                html={`${
                                  p?.shortDescription &&
                                  p.shortDescription.length > 0
                                    ? p?.shortDescription?.substring(0, 150)
                                    : (p?.fullDescription ?? '').substring(
                                        0,
                                        150,
                                      )
                                }...`}
                                tag="p"
                                className="small text-muted text-wrap"
                              />
                              <div className="d-flex pt-2 w-100 flex-sm-row flex-column align-items-center">
                                <div className="d-flex gap-3 small flex-grow-1 flex-sm-row flex-column">
                                  <span
                                    className={
                                      isLowQuantity && !deal.isPayByUser
                                        ? 'text-danger'
                                        : ''
                                    }
                                  >
                                    Available{' '}
                                    <strong>
                                      {deal.isPayByUser
                                        ? '∞'
                                        : p.quantityRemaining}
                                    </strong>
                                  </span>
                                  <span>
                                    Assigned{' '}
                                    <strong>{p.quantityAssigned}</strong>
                                  </span>
                                  {!deal.isPayByUser && (
                                    <span>
                                      Total <strong>{getNetQuantity(p)}</strong>
                                    </span>
                                  )}
                                </div>
                                <div className="d-block">
                                  <LinkButton
                                    titleText="More Details"
                                    size="xs"
                                    variant="light"
                                    className={`${agreementCardStyles.prodMoreDetailsLink} p-0`}
                                    onClick={() => onMoreDetailsClick(p)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="d-block">
                <AgreementSelectedProducts
                  products={selectedProducts}
                  deal={deal}
                  onClick={handleSubmit}
                  disabled={
                    displayedProducts.length === 0 ||
                    isSubmitting ||
                    selectedProducts.length === 0
                  }
                  buttonText="Add Products to Domain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {showProductModal && selectedProduct && (
        <ProductDetailModal
          onCancel={() => setShowProductModal(false)}
          product={selectedProduct}
        />
      )}
    </Modal>
  );
}
